// Apolo IDE — PdfViewerModal (sprint plan-tab-v1, E4).
//
// Full-screen PDF viewer com <embed> nativo. Esc fecha. Click no backdrop
// fecha. Download button + open-in-new-tab.

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, ExternalLink, X } from 'lucide-react';

export default function PdfViewerModal({ open, onClose, url, name = 'PDF' }) {
  useEffect(() => {
    function esc(e) { if (e.key === 'Escape' && open) onClose(); }
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={`Visualizador PDF — ${name}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-stretch justify-stretch bg-black/80 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 12, scale: 0.99 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 12, scale: 0.99 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="m-4 flex flex-1 flex-col overflow-hidden rounded-2xl border border-apolo-plan-accentBorder bg-apolo-surface1"
            style={{ boxShadow: 'var(--apolo-plan-glow-wide), 0 24px 80px rgba(0,0,0,0.5)' }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center gap-2 border-b border-apolo-borderSubtle px-4 py-2.5">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-base" style={{ background: 'var(--apolo-plan-accentMuted)', color: 'var(--apolo-plan-accent)' }} aria-hidden="true">📄</span>
              <span className="truncate text-sm font-medium text-apolo-textPrimary">{name}</span>
              <span className="ml-auto inline-flex items-center gap-1">
                {url ? (
                  <>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                      aria-label="Abrir em nova aba"
                    >
                      <ExternalLink size={12} /> Nova aba
                    </a>
                    <a
                      href={url}
                      download={name}
                      className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                      aria-label="Baixar"
                    >
                      <Download size={12} /> Baixar
                    </a>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <X size={14} />
                </button>
              </span>
            </div>
            {/* Body */}
            <div className="flex flex-1 overflow-hidden bg-apolo-surface2">
              {url ? (
                <embed src={url + '#zoom=page-width'} type="application/pdf" className="h-full w-full" aria-label={`Conteúdo PDF — ${name}`} />
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-apolo-textTertiary">PDF não disponível.</div>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
